import { ReactNode, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { HeaderBack } from './Header.Back';
import { HeaderIcon } from './Header.Icon';
import { HeaderTextButton } from './Header.TextButton';
import * as styles from './styles.css';

export interface HeaderProps {
  /**
   * @property {string} static 화면을 따라오지 않음
   * @property {string} sticky 화면을 따라옴
   * @property {string} responsive 스크롤을 내릴 때 사라지고, 올릴 때 나타남
   *
   * @default "sticky"
   */
  mode?: 'static' | 'sticky' | 'fixed' | 'responsive';
  children?: ReactNode;
  right?: ReactNode;
  /**
   * 뒤로가기 버튼을 숨기고 타이틀 영역을 왼쪽으로 이동시킵니다.
   */
  hideBack?: boolean;
  onBack?: () => void;

  tab?: ReactNode;
}

export const Header = (props: HeaderProps) => {
  const { mode = 'fixed', children, right, hideBack, onBack, tab } = props;
  const navigate = useNavigate();

  const [visible, setVisible] = useState(true);
  const [prevScrollTop, setPrevScrollTop] = useState(0);
  const [currentScrollTop, setCurrentScrollTop] = useState(0);

  useEffect(() => {
    if (mode !== 'responsive') {
      window.removeEventListener('scroll', initialScrollTop);
      return;
    }
    window.addEventListener('scroll', initialScrollTop);
    return () => {
      window.removeEventListener('scroll', initialScrollTop);
    };
  }, []);

  useEffect(() => {
    if (mode !== 'responsive') return;
    handleScroll(prevScrollTop, currentScrollTop);
  }, [mode, currentScrollTop]);

  const initialScrollTop = () => {
    setCurrentScrollTop(window.scrollY);
  };

  const handleScroll = (prevScrollTop: number, currentScrollTop: number) => {
    if (currentScrollTop < 55) {
      setVisible(true);
      setPrevScrollTop(currentScrollTop);
      return;
    }

    // 스크롤을 위로 향할 경우
    if (prevScrollTop - 1 > currentScrollTop) {
      setVisible(true);
    }

    // 스크롤을 아래로 향할 경우
    if (prevScrollTop + 1 < currentScrollTop) {
      setVisible(false);
    }

    setPrevScrollTop(currentScrollTop);
  };

  const handleBack = () => {
    if (!onBack) {
      navigate(-1);
      return;
    }
    onBack();
  };

  const position = useMemo(() => {
    switch (mode) {
      case 'responsive':
        return 'fixed';
      case 'static':
        return 'static';
      case 'sticky':
      default:
        return 'sticky';
    }
  }, [mode]);

  return (
    <>
      <div className={styles.headerContaner({ mode, hide: !visible })}>
        <div className={styles.header({})}>
          {!hideBack && (
            <div className={styles.containerLeft}>
              <HeaderBack onClick={handleBack} />
            </div>
          )}

          <div className={styles.containerTitle({ align: hideBack ? 'left' : 'center' })}>
            {children}
          </div>
          <div className={styles.containerRight}>{right}</div>
        </div>
        {tab}
      </div>
      {position !== 'static' && <div className={styles.headerPlaceholder({ withTab: !!tab })} />}
    </>
  );
};

Header.TextButton = HeaderTextButton;
Header.Icon = HeaderIcon;
